import {
  useEffect,
  useState,
} from 'react';

import { useGetShowtimesQuery } from '../store';
import { parseFriendlyDateAndHour } from '../utils/dates';
import HorizontalTabs from './atoms/HorizontalTabs';
import EventDTO from '../models/event';
import TabsSkeleton from './atoms/TabsSkeleton';

interface ShowtimeTabsProps {
  event: EventDTO;
  onTabChange: (showtimeId: string) => void;
  selectedTab?: string;
  disabled?: boolean;
}

const ShowtimeTabs: React.FC<ShowtimeTabsProps> = ({
  event, onTabChange, selectedTab, disabled,
}) => {
  const [currentTab, setCurrentTab] = useState(selectedTab || '');

  const { data: showtimes, isLoading } = useGetShowtimesQuery(
    { businessId: event.business_id, eventId: event.id },
    { skip: !event.has_showtimes },
  );

  const sortedShowtimes = showtimes?.slice().filter((showtime) => showtime.active).sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());

  useEffect(() => {
    onTabChange(currentTab);
  }, [currentTab, onTabChange]);

  useEffect(() => {
    // If we have showtimes and no tab is selected, select the first one.
    if (sortedShowtimes && sortedShowtimes.length > 0 && !currentTab) {
      setCurrentTab(sortedShowtimes[0].id);
    }
    // if a tab is selected that is no longer in showtimes, reset.
    if (currentTab && sortedShowtimes && !sortedShowtimes.find((st) => st.id === currentTab)) {
      setCurrentTab(sortedShowtimes.length > 0 ? sortedShowtimes[0].id : '');
    }
  }, [sortedShowtimes, currentTab]);

  const handleTabClick = (showtimeId: string) => {
    setCurrentTab(showtimeId);
  };

  const tabs = sortedShowtimes?.map((showtime) => ({
    label: parseFriendlyDateAndHour(showtime.starts_at, true),
    onClick: () => handleTabClick(showtime.id),
    selected: currentTab === showtime.id,
  })) || [];

  if (isLoading) {
    return <TabsSkeleton />;
  }

  if (!event.has_showtimes || !showtimes || showtimes.length === 0) {
    return null;
  }

  return <HorizontalTabs tabs={tabs} disabled={disabled} />;
};

export default ShowtimeTabs;
